import type { SyntheticEvent } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Grid, Stack, Tab, Tabs } from '@mui/material';
import {
  FactCheck as VerificationIcon,
  Groups as UsersIcon,
  Route as PairsIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getAdminStats } from '@/services/api';
import { MetricCard, PageShell } from '@/components/common/Page';
import AdminAnnouncements from '@/components/admin/AdminAnnouncements';
import AdminBotPosts from '@/components/admin/AdminBotPosts';
import AdminCircles from '@/components/admin/AdminCircles';
import AdminLanguageSelect from '@/components/admin/AdminLanguageSelect';
import AdminOperations from '@/components/admin/AdminOperations';
import AdminPairsManager from '@/components/admin/AdminPairsManager';
import AdminTimeZoneSelect from '@/components/admin/AdminTimeZoneSelect';
import AdminUsers from '@/components/admin/AdminUsers';
import AdminVerifications from '@/components/admin/AdminVerifications';
import { useAdminI18n } from '@/contexts/AdminI18nContext';
import { AdminTimeProvider } from '@/contexts/AdminTimeContext';

export default function AdminPage() {
  return (
    <AdminTimeProvider>
      <AdminPageContent />
    </AdminTimeProvider>
  );
}

function AdminPageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAdminI18n();

  const getTabValue = () => {
    if (location.pathname.includes('/admin/verifications')) return 1;
    if (location.pathname.includes('/admin/signals')) return 2;
    if (location.pathname.includes('/admin/users')) return 3;
    if (location.pathname.includes('/admin/bot-posts')) return 4;
    if (location.pathname.includes('/admin/announcements')) return 5;
    if (location.pathname.includes('/admin/operations')) return 6;
    return 0;
  };

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: getAdminStats,
  });

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    switch (newValue) {
      case 0:
        navigate('/admin');
        break;
      case 1:
        navigate('/admin/verifications');
        break;
      case 2:
        navigate('/admin/signals');
        break;
      case 3:
        navigate('/admin/users');
        break;
      case 4:
        navigate('/admin/bot-posts');
        break;
      case 5:
        navigate('/admin/announcements');
        break;
      case 6:
        navigate('/admin/operations');
        break;
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100dvh',
        minHeight: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
        <PageShell
          eyebrow={t('admin.page.eyebrow')}
          title={t('admin.page.title')}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <AdminLanguageSelect />
              <AdminTimeZoneSelect />
            </Stack>
          }
          reserveBottomNav={false}
        >
        {statsLoading ? (
          <CircularProgress size={24} />
        ) : (
          statsData?.stats && (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <MetricCard
                  label={t('admin.page.metricUsers')}
                  value={statsData.stats.total_users}
                  icon={<UsersIcon fontSize="small" />}
                  tone="info"
                />
              </Grid>
              <Grid item xs={4}>
                <MetricCard
                  label={t('admin.page.metricPending')}
                  value={statsData.stats.pending_verifications}
                  icon={<VerificationIcon fontSize="small" />}
                  tone="warning"
                />
              </Grid>
              <Grid item xs={4}>
                <MetricCard
                  label={t('admin.page.metricActivePairs')}
                  value={statsData.stats.active_pairs}
                  icon={<PairsIcon fontSize="small" />}
                  tone="success"
                />
              </Grid>
            </Grid>
          )
        )}

        <Box
          sx={{
            p: 0.5,
            mb: 2,
            borderRadius: 999,
            bgcolor: 'rgba(244, 255, 249, 0.045)',
            border: '1px solid rgba(226, 255, 238, 0.12)',
          }}
        >
          <Tabs
            value={getTabValue()}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={t('admin.page.tabPairs')} />
            <Tab
              label={`${t('admin.page.tabVerifications')}${
                statsData?.stats?.pending_verifications ? ` (${statsData.stats.pending_verifications})` : ''
              }`}
            />
            <Tab label={t('admin.page.tabSignals')} />
            <Tab label={t('admin.page.tabUsers')} />
            <Tab label={t('admin.page.tabBotPosts')} />
            <Tab label={t('admin.page.tabAnnouncements')} />
            <Tab label={t('admin.page.tabOperations')} />
          </Tabs>
        </Box>

        <Routes>
          <Route index element={<AdminPairsManager />} />
          <Route path="verifications" element={<AdminVerifications />} />
          <Route path="signals" element={<AdminCircles />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="bot-posts" element={<AdminBotPosts />} />
          <Route path="announcements" element={<AdminAnnouncements />} />
          <Route path="operations" element={<AdminOperations />} />
        </Routes>
        </PageShell>
      </Box>
    </Box>
  );
}
